import React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { SidePanel, type SidePanelItem, SidePanelSheetSkeleton } from '@gitbook/react-uikit';

import { GitSyncSidePanel } from '../../../Components';
import { useNavigation } from '../../../navigation';
// import { useBrokenLinksFeatureAccess } from '../../../state';
import { ChangeRequestAgentSidePanel } from '../Agent';
import { ChangeRequestSidePanel, ChangeRequestsListSidePanel } from '../ChangeRequest';
import { SpaceCommentsSidePanel } from '../Comments';
import { HistorySidePanel } from '../History';
import { PageOptionsSidePanel } from '../Page';
import { VariablesSidePanel } from '../Variables';
import { WithSpaceHiveContext } from './WithSpaceHiveContext';

/**
 * Centralized controller for the side panels visible on the Space screen.
 *
 * It handles side panels that are driven by navigation, as well as any in-memory side panels.
 */
export function SpaceScreenSidePanel(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    children: React.ReactNode;
    screen: NavigationScreenSpaceState;
    changeRequest: api.ChangeRequest | null;
    readOnly: boolean;
    showGitSyncErrorModal: boolean;
}): React.ReactElement {
    const { space, changeRequest, screen, analyticsContext, children, readOnly } = props;
    const { navigate } = useNavigation();
    // const hasBrokenLinksFeature = useBrokenLinksFeatureAccess({ isInChangeRequest: false });

    const onCloseSideSheet = () => {
        navigate({
            current: {
                sideSheet: undefined,
            },
        });
    };

    // Determine the current side panel, based on the navigation screen
    // and other state.
    const sidePanel: SidePanelItem | null = (() => {
        if (
            screen.revision &&
            // https://linear.app/gitbook-x/issue/RND-1988/explore-allowing-readers-to-access-history-and-change-requests
            space.permissions.edit
        ) {
            return {
                children: (
                    <HistorySidePanel
                        space={space}
                        changeRequest={changeRequest}
                        screen={screen}
                        analyticsContext={analyticsContext}
                    />
                ),
            };
        }

        if (screen.sideSheet?.type === 'audits') {
            // We're temporarily disabling the audits side panel
            // because content refs indexation's current implementation is causing
            // huge performance issues on our PlanetScale DB.
            return null;

            // if (!hasBrokenLinksFeature) {
            //     return null;
            // }
            // return {
            //     children: (
            //         <AuditsSidePanel
            //             changeRequest={changeRequest}
            //             space={space}
            //             screen={screen}
            //             analyticsContext={analyticsContext}
            //         />
            //     ),
            // };
        }

        if (
            space.gitSync?.installationProvider &&
            screen.sideSheet?.type === 'gitsync' &&
            screen.sideSheet.tab !== 'progress' &&
            space.permissions.admin
        ) {
            return {
                size: 'large',
                children: (
                    <GitSyncSidePanel
                        space={space}
                        screen={screen}
                        analyticsContext={analyticsContext}
                    />
                ),
            };
        }

        if (changeRequest && screen.sideSheet?.type === 'overview') {
            return {
                children: <ChangeRequestSidePanel changeRequest={changeRequest} space={space} />,
            };
        }

        if (changeRequest && screen.sideSheet?.type === 'agent') {
            return {
                children: (
                    <WithSpaceHiveContext
                        space={space}
                        changeRequest={changeRequest}
                        readOnly={readOnly}
                        screen={screen}
                    >
                        <ChangeRequestAgentSidePanel
                            changeRequest={changeRequest}
                            space={space}
                            analyticsContext={analyticsContext}
                            screen={screen}
                        />
                    </WithSpaceHiveContext>
                ),
            };
        }

        if (
            screen.sideSheet?.type === 'change-requests' &&
            // https://linear.app/gitbook-x/issue/RND-1988/explore-allowing-readers-to-access-history-and-change-requests
            space.permissions.edit
        ) {
            return {
                children: (
                    <ChangeRequestsListSidePanel
                        space={space}
                        analyticsContext={analyticsContext}
                    />
                ),
            };
        }

        if (screen.sideSheet?.type === 'comments' && space.permissions.comment) {
            return {
                children: (
                    <React.Suspense
                        fallback={<SidePanelSheetSkeleton onClose={onCloseSideSheet} />}
                    >
                        <WithSpaceHiveContext
                            space={space}
                            changeRequest={changeRequest}
                            readOnly={readOnly}
                            screen={screen}
                        >
                            <SpaceCommentsSidePanel
                                space={space}
                                changeRequest={changeRequest}
                                analyticsContext={analyticsContext}
                                screen={screen}
                            />
                        </WithSpaceHiveContext>
                    </React.Suspense>
                ),
            };
        }

        if (screen.sideSheet?.type === 'page' && !readOnly) {
            return {
                children: (
                    <PageOptionsSidePanel
                        space={space}
                        analyticsContext={analyticsContext}
                        readOnly={readOnly}
                        screen={{
                            ...screen,
                            sideSheet: screen.sideSheet,
                        }}
                    />
                ),
            };
        }

        if (screen.sideSheet?.type === 'variables') {
            return {
                children: (
                    <React.Suspense fallback={<SidePanelSheetSkeleton />}>
                        <WithSpaceHiveContext
                            space={space}
                            changeRequest={changeRequest}
                            readOnly={readOnly}
                            screen={screen}
                        >
                            <VariablesSidePanel
                                analyticsContext={analyticsContext}
                                screen={screen}
                                readOnly={readOnly}
                            />
                        </WithSpaceHiveContext>
                    </React.Suspense>
                ),
            };
        }

        return null;
    })();

    return (
        <SidePanel sidePanel={sidePanel} animationKey={space.id}>
            {children}
        </SidePanel>
    );
}

/**
 * Return true if the space screen should display a side panel.
 */
export function shouldDisplaySidePanel(screen: NavigationScreenSpaceState): boolean {
    return (
        !!screen.revision ||
        screen.sideSheet?.type === 'page' ||
        (screen.sideSheet?.type === 'gitsync' && screen.sideSheet.tab !== 'progress') ||
        // screen.sideSheet?.type === 'audits' ||
        screen.sideSheet?.type === 'overview' ||
        screen.sideSheet?.type === 'agent' ||
        screen.sideSheet?.type === 'change-requests' ||
        screen.sideSheet?.type === 'comments' ||
        screen.sideSheet?.type === 'variables'
    );
}
